import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseOutlined";
import LiveTvIcon from "@mui/icons-material/LiveTvOutlined";
import MovieIcon from "@mui/icons-material/MovieCreationOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForwardOutlined";
import FolderIcon from "@mui/icons-material/PermMediaOutlined";
import { useMemo, useState } from "react";
import type {
  Asset,
  AssetType,
  Genre,
  Provider,
} from "@shared/types/content-api";
import {
  assetToDraft,
  draftAsAsset,
  draftRefusals,
  emptiedCollections,
  emptyAssetDraft,
  type AssetDraft,
} from "@shared/asset-draft";
import { assetPlaybackBlockers } from "@shared/channel-playability";
import { availabilityFor, rightsIssues } from "@shared/content-rights";
import { imageIssues } from "@shared/vod-images";
import {
  localisedIssues,
  localisedStatus,
  localisedSummary,
} from "@shared/vod-localised";
import {
  VOD_ASSET_TYPES,
  durationIssues,
  genreDisplayName,
  yearIssues,
} from "@shared/vod-title";
import { tokens } from "../../theme";
import AvailabilityChip from "./AvailabilityChip";
import AvailabilityEditor from "./AvailabilityEditor";
import ChannelEpgMapping from "./ChannelEpgMapping";
import ChannelSchedulePreview from "./ChannelSchedulePreview";
import MediaFilesPanel from "./MediaFilesPanel";
import LocalisedContentPanel from "./LocalisedContentPanel";
import LocalisedImagesPanel from "./LocalisedImagesPanel";
import MediasEditor from "./MediasEditor";
import {
  Field,
  SectionHead,
  SoftChip,
  StatusDot,
  StringListField,
} from "./editorBits";
import {
  blockerText,
  durationIssueText,
  imageIssueText,
  localisedIssueText,
  rightsIssueText,
  saveErrorText,
  yearIssueText,
} from "./findings";
import TitleOwnerSection from "./TitleOwnerSection";

/**
 * The Distribution editor - one dialog, two bodies (`BE-15`).
 *
 * ## Why one component and not two
 *
 * A channel and a VOD title are the **same row upstream** - `CmsAsset`, with
 * `type: "channel"` or `type: "movie"`. The server learned this the expensive
 * way and put its media readers, refusals, wipe guard and provider check in one
 * shared boundary (`contentAssetBody.ts`), because two copies would mean one
 * half of the catalogue silently accepting what the other half refuses. This is
 * that argument one layer up: two look-alike dialogs over one row is how the
 * channel editor ends up able to express something the title editor cannot.
 *
 * What genuinely differs is the middle: a channel is a feed you carry
 * (distributor metadata plus the media a player tunes), a title is a work you
 * shelve (year, length, studio, and its place in a series). Everything around
 * that middle - localised text, artwork, media, licence windows, the derived
 * verdicts - is identical, and identical is how it is written here.
 *
 * ## Seeded once, remounted by key
 *
 * ```tsx
 * <AssetEditorDialog key={editing?.assetId ?? "new"} initial={editing} … />
 * ```
 *
 * The caller changes the key; the dialog seeds itself once and never watches
 * props. A prop-watching reset cannot tell *"opened a different record"* from
 * *"reopened the same one"*, so it either keeps stale state or discards live
 * edits depending on which row you picked - and both are invisible until
 * somebody saves.
 *
 * ⚠️ This matters **more** now that the initial value is fetched, not less. An
 * async load means props arrive after the first render, which is exactly the
 * moment a "did it change?" check has to guess.
 *
 * ## Every verdict on screen is DERIVED
 *
 * The rail draws `assetPlaybackBlockers`, `availabilityFor`, `imageIssues`,
 * `localisedIssues`, `yearIssues` and `durationIssues` over the draft in hand -
 * the same functions the list and the server call, over the same shapes. None
 * of it is stored, and none of it is recomputed by a second rule that would
 * start agreeing by luck. That is why a half-typed form is a legitimate
 * argument to all six.
 */

export type AssetEditorKind = "channel" | "title";

export interface AssetEditorDialogProps {
  open: boolean;
  kind: AssetEditorKind;
  /** The row being edited, or null to create. Whole assets only - a row IS its draft. */
  initial: Asset | null;
  providers: Provider[];
  genres: Genre[];
  saving?: boolean;
  /** The server's refusal, as it came back. */
  error?: { code?: string; message: string } | null;
  onClose: () => void;
  onSubmit: (
    draft: AssetDraft,
    options: { confirmEmptyCollections: boolean }
  ) => void;
}

function TypeIcon({ kind }: { kind: AssetEditorKind }) {
  const Icon = kind === "channel" ? LiveTvIcon : MovieIcon;
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 2,
        bgcolor: tokens.brandTintSoft,
        display: "flex",
        flex: "0 0 auto",
      }}
    >
      <Icon sx={{ fontSize: 34, color: tokens.brand }} />
    </Box>
  );
}

function ManageLink({
  onClick,
  testId,
}: {
  onClick: () => void;
  testId: string;
}) {
  return (
    <Button
      size="small"
      onClick={onClick}
      data-testid={testId}
      endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
      sx={{ minHeight: 0, px: 0, fontSize: 14, fontWeight: 600 }}
    >
      Manage
    </Button>
  );
}

/** One derived finding, drawn the same way wherever it came from. */
function Finding({ text, tone }: { text: string; tone: "blocker" | "issue" }) {
  return (
    <Typography
      sx={{
        fontSize: 12,
        color: tone === "blocker" ? tokens.error : tokens.inkMuted,
        lineHeight: 1.5,
      }}
    >
      • {text}
    </Typography>
  );
}

export default function AssetEditorDialog({
  open,
  kind,
  initial,
  providers,
  genres,
  saving = false,
  error = null,
  onClose,
  onSubmit,
}: AssetEditorDialogProps) {
  const [draft, setDraft] = useState<AssetDraft>(() =>
    initial
      ? assetToDraft(initial)
      : emptyAssetDraft(kind === "channel" ? "channel" : "movie")
  );
  const [contentPanel, setContentPanel] = useState(false);
  const [imagesPanel, setImagesPanel] = useState(false);
  const [mediaFiles, setMediaFiles] = useState(false);
  /** Set once the operator has been shown, and accepted, what a save will delete. */
  const [wipeAccepted, setWipeAccepted] = useState(false);

  const isEdit = initial !== null;
  const set = <K extends keyof AssetDraft>(key: K, next: AssetDraft[K]) =>
    setDraft((prev) => ({ ...prev, [key]: next }));

  const derived = useMemo(() => draftAsAsset(draft, initial), [draft, initial]);
  const blockers = useMemo(
    () =>
      assetPlaybackBlockers(
        derived,
        kind === "channel" ? ["channel", "main"] : ["main"]
      ),
    [derived, kind]
  );
  const availability = useMemo(() => availabilityFor(derived), [derived]);
  const refusals = draftRefusals(draft);
  /**
   * Which collections this save would EMPTY.
   *
   * Named before the operator is asked rather than after the server refuses:
   * upstream a restated `[]` DELETES every row in that collection, and "the
   * panel failed to load" and "I meant to remove all of them" arrive on the
   * wire as the same payload.
   */
  const wiping = useMemo(
    () => (initial ? emptiedCollections(draft, initial) : []),
    [draft, initial]
  );

  const localisedRows = draft.localizedInfo;
  const doneLanguages = localisedRows.filter(
    (row) => localisedStatus(row) === "done"
  ).length;

  const seriesFields = kind === "title" && draft.type !== "movie";

  function submit() {
    if (wiping.length && !wipeAccepted) {
      setWipeAccepted(true);
      return;
    }
    onSubmit(draft, { confirmEmptyCollections: wiping.length > 0 });
  }

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { maxWidth: 1380, m: 2 } }}
      data-testid={`edit-${kind}-dialog`}
    >
      <Stack
        direction="row"
        alignItems="flex-start"
        spacing={2}
        sx={{ p: 4, pb: 3 }}
      >
        <TypeIcon kind={kind} />
        <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
          <Typography
            sx={{ fontSize: 24, fontWeight: 600, color: tokens.ink, mb: 0.75 }}
          >
            {isEdit
              ? kind === "channel"
                ? "Edit channel"
                : "Edit title"
              : kind === "channel"
                ? "Create channel"
                : "Create new title"}
          </Typography>
          <Typography sx={{ fontSize: 14, color: tokens.inkMuted }}>
            {kind === "channel"
              ? "A live channel is a feed the platform carries: the distributor's metadata, plus the media a player tunes."
              : "A title is a work on the on-demand shelf. Seasons and episodes are their own rows, joined by ids."}
          </Typography>
        </Box>
        {isEdit && initial && (
          <Stack spacing={0.5} alignItems="flex-end" sx={{ pt: 0.5 }}>
            <AvailabilityChip availability={availability} />
            <Typography
              sx={{
                fontSize: 11,
                color: tokens.inkMuted,
                fontFamily: "monospace",
              }}
            >
              {initial.assetId}
            </Typography>
          </Stack>
        )}
        <IconButton onClick={onClose} aria-label="Close" sx={{ mt: -0.5 }}>
          <CloseIcon />
        </IconButton>
      </Stack>
      <Divider />

      <DialogContent sx={{ p: 4 }}>
        <Stack direction={{ xs: "column", lg: "row" }} spacing={6}>
          <Stack spacing={6} sx={{ flex: "1 1 auto", minWidth: 0 }}>
            {/* ---- Basics ---- */}
            <Box>
              <SectionHead
                title="Basics"
                subtitle={
                  kind === "channel"
                    ? "What the distributor calls this feed, and who supplies it."
                    : "What the catalogue calls this work, and who supplies it."
                }
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(3, minmax(0, 1fr))",
                  },
                  gap: 3.5,
                }}
              >
                <Field
                  label="Name"
                  placeholder="Enter the name..."
                  value={draft.originalTitle}
                  onChange={(e) => set("originalTitle", e.target.value)}
                  inputProps={{ "data-testid": "asset-name" }}
                  required
                />
                <Field
                  label="Distributor id"
                  placeholder="The supplier's own id"
                  value={draft.externalId}
                  onChange={(e) => set("externalId", e.target.value)}
                  inputProps={{ "data-testid": "asset-external-id" }}
                  helperText="Not unique upstream, so it is a lookup and never a key. Foyer will not invent one."
                  required
                />
                <Field
                  select
                  label="Provider"
                  value={draft.providerId}
                  onChange={(e) => set("providerId", e.target.value)}
                  SelectProps={{ displayEmpty: true }}
                  inputProps={{ "data-testid": "asset-provider" }}
                >
                  <MenuItem value="">
                    <em>No provider</em>
                  </MenuItem>
                  {providers.map((p) => (
                    <MenuItem key={p.providerId} value={p.providerId}>
                      {p.name || p.providerId}
                    </MenuItem>
                  ))}
                </Field>

                {kind === "title" && (
                  <Field
                    select
                    label="Type"
                    value={draft.type}
                    disabled={isEdit}
                    onChange={(e) => set("type", e.target.value as AssetType)}
                    helperText={
                      isEdit
                        ? "Fixed at creation upstream. Re-classifying means a new row of the right type."
                        : "A season and an episode are rows of their own, not fields on a series."
                    }
                  >
                    {VOD_ASSET_TYPES.map((t) => (
                      <MenuItem key={t} value={t}>
                        {t[0].toUpperCase() + t.slice(1)}
                      </MenuItem>
                    ))}
                  </Field>
                )}

                <Field
                  label="Original language"
                  placeholder="sv"
                  value={draft.originalLanguage}
                  onChange={(e) => set("originalLanguage", e.target.value)}
                />
                <Field
                  label="Country of origin"
                  placeholder="se"
                  value={draft.countryOfOrigin}
                  onChange={(e) => set("countryOfOrigin", e.target.value)}
                />

                {kind === "title" && (
                  <>
                    <Field
                      label="Production year"
                      placeholder="e.g. 2024"
                      value={draft.productionYear}
                      onChange={(e) => set("productionYear", e.target.value)}
                      helperText="The year that reaches a device."
                    />
                    <Field
                      label="Length (seconds)"
                      placeholder="e.g. 5520"
                      value={draft.duration}
                      onChange={(e) => set("duration", e.target.value)}
                      helperText="Seconds — a media's own duration is milliseconds."
                      inputProps={{ "data-testid": "asset-duration" }}
                    />
                    <Field
                      label="Studio"
                      value={draft.studio}
                      onChange={(e) => set("studio", e.target.value)}
                    />
                  </>
                )}

                <Field
                  label="Copyright information"
                  value={draft.copyrightInfo}
                  onChange={(e) => set("copyrightInfo", e.target.value)}
                />
                <Field
                  label="EIDR id"
                  value={draft.eidrId}
                  onChange={(e) => set("eidrId", e.target.value)}
                />
              </Box>
            </Box>

            {/* ---- Localised content ---- */}
            <Box>
              <SectionHead
                title="Localised content"
                subtitle="Titles and descriptions per language. The key is the language, and a language may appear once."
                aside={
                  <SoftChip
                    label={`${doneLanguages} / ${localisedRows.length} languages complete`}
                  />
                }
              />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                  },
                  columnGap: 3.5,
                  rowGap: 1,
                }}
              >
                {localisedRows.length === 0 && (
                  <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                    No localised text. A device that finds none for its language
                    falls back to English, all-or-nothing.
                  </Typography>
                )}
                {localisedRows.map((row) => (
                  <Stack
                    key={row.language}
                    direction="row"
                    alignItems="center"
                    spacing={4}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <StatusDot status={localisedStatus(row)} />
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 700,
                          color: tokens.ink,
                        }}
                      >
                        {row.language.toUpperCase()}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                      {localisedSummary(row)}
                    </Typography>
                  </Stack>
                ))}
              </Box>
              <Stack direction="row" justifyContent="flex-end">
                <ManageLink
                  onClick={() => setContentPanel(true)}
                  testId="manage-content"
                />
              </Stack>
            </Box>

            {/* ---- Keywords & genres ---- */}
            <Box>
              <SectionHead
                title="Keywords & genres"
                subtitle="Genres are entities the catalogue owns; keywords are the one free-text list on a row."
                aside={
                  <>
                    <SoftChip label={`${draft.genreIds.length} genres`} />
                    <SoftChip label={`${draft.keywords.length} keywords`} />
                  </>
                }
              />
              <Stack spacing={3}>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: tokens.ink,
                      mb: 1,
                    }}
                  >
                    Genres
                  </Typography>
                  <Field
                    select
                    fullWidth
                    label=""
                    value={draft.genreIds}
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) => (
                        <Stack
                          direction="row"
                          spacing={0.5}
                          flexWrap="wrap"
                          useFlexGap
                        >
                          {(selected as string[]).map((id) => (
                            <Chip
                              key={id}
                              size="small"
                              label={genreDisplayName(
                                genres.find((g) => g.genreId === id) ?? {
                                  genreId: id,
                                  name: id,
                                  localizedName: [],
                                }
                              )}
                            />
                          ))}
                        </Stack>
                      ),
                    }}
                    onChange={(e) =>
                      set(
                        "genreIds",
                        typeof e.target.value === "string"
                          ? e.target.value.split(",")
                          : (e.target.value as unknown as string[])
                      )
                    }
                    inputProps={{ "data-testid": "asset-genres" }}
                  >
                    {genres.map((genre) => (
                      <MenuItem key={genre.genreId} value={genre.genreId}>
                        {genreDisplayName(genre)}
                      </MenuItem>
                    ))}
                  </Field>
                  <Typography
                    sx={{ fontSize: 11, color: tokens.inkMuted, mt: 0.5 }}
                  >
                    Picked, never typed: `Genre.name` is unique upstream, so an
                    invented one is a permanent second shelf in a catalogue
                    Foyer does not own.
                  </Typography>
                </Box>
                <StringListField
                  label="Keywords"
                  hint="Two spellings of one word are two rows upstream, and both have to be searched for."
                  value={draft.keywords}
                  placeholder="nordic noir"
                  onChange={(next) => set("keywords", next)}
                  testId="asset-keyword"
                />
              </Stack>
            </Box>

            {/* ---- Series information ---- */}
            {seriesFields && (
              <Box>
                <SectionHead
                  title="Series information"
                  subtitle="Seasons and episodes are SIBLING ROWS joined by these ids. There is no episode list on a series, and no foreign key on either id."
                />
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      md: "1fr 1fr 140px 140px",
                    },
                    gap: 3.5,
                  }}
                >
                  <Field
                    label="Series id"
                    placeholder="The parent series' assetId"
                    value={draft.seriesId}
                    onChange={(e) => set("seriesId", e.target.value)}
                    disabled={draft.type === "series"}
                    helperText={
                      draft.type === "series"
                        ? "A series is its own parent; children point at this row."
                        : undefined
                    }
                  />
                  <Field
                    label="Season id"
                    placeholder="The parent season's assetId"
                    value={draft.seasonId}
                    onChange={(e) => set("seasonId", e.target.value)}
                    disabled={draft.type !== "episode"}
                  />
                  <Field
                    label="Season no."
                    value={draft.seasonNumber}
                    onChange={(e) => set("seasonNumber", e.target.value)}
                    disabled={draft.type === "series"}
                  />
                  <Field
                    label="Episode no."
                    value={draft.episodeNumber}
                    onChange={(e) => set("episodeNumber", e.target.value)}
                    disabled={draft.type !== "episode"}
                  />
                </Box>
              </Box>
            )}

            {/* ---- Artwork ---- */}
            <Box>
              <SectionHead
                title="Localised images"
                subtitle="Six types, and a language may carry several of one type — nothing on the image table is unique."
                aside={<SoftChip label={`${draft.images.length} images`} />}
              />
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                {draft.images.length === 0 && (
                  <Typography sx={{ fontSize: 12, color: tokens.inkMuted }}>
                    No artwork. A list row will draw a generated tile rather
                    than an image that 404s.
                  </Typography>
                )}
                {[...new Set(draft.images.map((i) => i.language))].map(
                  (language) => (
                    <SoftChip
                      key={language}
                      label={`${language.toUpperCase()}: ${
                        draft.images.filter((i) => i.language === language)
                          .length
                      }`}
                    />
                  )
                )}
              </Stack>
              <Stack direction="row" justifyContent="flex-end">
                <ManageLink
                  onClick={() => setImagesPanel(true)}
                  testId="manage-images"
                />
              </Stack>
            </Box>

            {/* ---- Media ---- */}
            <Box>
              <SectionHead
                title="Media"
                subtitle="Every playable rendition, each with its own resolution, languages and stream URLs."
                aside={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SoftChip label={`${draft.medias.length} medias`} />
                    {/* The way IN to ingest, and it only exists on a saved
                        asset: a job is keyed on assetId, and an asset being
                        created does not have one yet. */}
                    {initial && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<FolderIcon />}
                        onClick={() => setMediaFiles(true)}
                        data-testid="open-media-files"
                      >
                        Media files
                      </Button>
                    )}
                  </Stack>
                }
              />
              <MediasEditor
                medias={draft.medias}
                defaultType={kind === "channel" ? "channel" : "main"}
                onChange={(next) => set("medias", next)}
              />
            </Box>

            {/* ---- Programme guide (BE-27) ----
                Read-only, and deliberately HERE rather than on a page of its
                own: nobody authors EPG, so the only question a CMS schedule
                surface answers is "did this channel's mapping produce a
                schedule, and does it look right?" - which belongs beside the
                mapping that decides it (`BE-22` owns that field). A channel
                being created has no assetId and nothing to read yet. */}
            {/* CONTENT-28: which studio's portal shows this title. */}
            {kind === "title" && initial && (
              <TitleOwnerSection assetId={initial.assetId} />
            )}
            {kind === "channel" && initial && (
              <>
                {/* The mapping first, then what it produced (`BE-22` above
                    `BE-27`). An empty schedule is meaningless without the field
                    that decides it: the commonest reason a guide is blank is
                    that nothing was ever mapped, and nothing said so. */}
                <ChannelEpgMapping
                  assetId={initial.assetId}
                  alternativeIds={initial.alternativeIds}
                />
                <ChannelSchedulePreview assetId={initial.assetId} />
              </>
            )}

            {/* ---- Availability ---- */}
            <Box>
              <SectionHead
                title="Availability"
                subtitle="Licence windows. This is the whole of publishing — there is no publish flag in this catalogue."
                aside={<AvailabilityChip availability={availability} />}
              />
              <AvailabilityEditor
                rows={draft.contentRights}
                onChange={(next) => set("contentRights", next)}
              />
            </Box>
          </Stack>

          {/* ---- The rail: everything derived, nothing stored ---- */}
          <Box sx={{ flex: "0 0 auto", width: { xs: "100%", lg: 359 } }}>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 600,
                color: tokens.ink,
                mb: 0.75,
              }}
            >
              Summary
            </Typography>
            <Typography sx={{ fontSize: 12, color: tokens.inkMuted, mb: 2.5 }}>
              Worked out from what is on screen, every time it changes. None of
              it is stored — a verdict about data four writers can change is
              stale the moment it is written down.
            </Typography>

            <Stack spacing={2.5}>
              <Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: tokens.ink,
                    mb: 1,
                  }}
                >
                  Fit to offer a guest
                </Typography>
                {blockers.length === 0 ? (
                  <Typography sx={{ fontSize: 12, color: tokens.success }}>
                    Nothing blocking playback.
                  </Typography>
                ) : (
                  <Stack spacing={0.5} data-testid="editor-blockers">
                    {blockers.map((blocker, i) => (
                      <Finding
                        key={`${blocker.code}-${i}`}
                        tone="blocker"
                        text={blockerText(blocker)}
                      />
                    ))}
                  </Stack>
                )}
              </Box>

              <Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: tokens.ink,
                    mb: 1,
                  }}
                >
                  Worth knowing
                </Typography>
                <Stack spacing={0.5} data-testid="editor-issues">
                  {rightsIssues(derived).map((issue, i) => (
                    <Finding
                      key={`r${i}`}
                      tone="issue"
                      text={rightsIssueText(issue)}
                    />
                  ))}
                  {localisedIssues(derived).map((issue, i) => (
                    <Finding
                      key={`l${i}`}
                      tone="issue"
                      text={localisedIssueText(issue)}
                    />
                  ))}
                  {imageIssues(derived).map((issue, i) => (
                    <Finding
                      key={`i${i}`}
                      tone="issue"
                      text={imageIssueText(issue)}
                    />
                  ))}
                  {kind === "title" &&
                    yearIssues(derived).map((issue, i) => (
                      <Finding
                        key={`y${i}`}
                        tone="issue"
                        text={yearIssueText(issue)}
                      />
                    ))}
                  {kind === "title" &&
                    durationIssues(derived).map((issue, i) => (
                      <Finding
                        key={`d${i}`}
                        tone="issue"
                        text={durationIssueText(issue)}
                      />
                    ))}
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <Divider />
      <Box sx={{ px: 4, pt: 2 }}>
        {refusals.length > 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {
              refusals.map((r) => {
                if (r.field === "externalId")
                  return "A distributor id is required.";
                if (r.field === "originalTitle") return "A name is required.";
                if (r.field === "resourceUrl")
                  return `Media ${r.mediaIndex + 1} has a stream with no URL.`;
                return `Image ${r.index + 1} has no language.`;
              })[0]
            }
          </Alert>
        )}
        {wiping.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }} data-testid="wipe-warning">
            Saving will DELETE every row in: {wiping.join(", ")}. Upstream a
            restated empty collection removes what it does not name.{" "}
            {wipeAccepted
              ? "Press Save again to confirm."
              : "Press Save to be asked once more."}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} data-testid="editor-error">
            {saveErrorText(error.code, error.message)}
          </Alert>
        )}
      </Box>
      <DialogActions sx={{ p: 4, pt: 2, justifyContent: "space-between" }}>
        <Button variant="outlined" onClick={onClose} sx={{ width: 124 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={saving || refusals.length > 0}
          sx={{ minWidth: 155 }}
          data-testid="asset-submit"
        >
          {saving
            ? "Saving..."
            : isEdit
              ? "Save changes"
              : kind === "channel"
                ? "Create channel"
                : "Create title"}
        </Button>
      </DialogActions>

      <LocalisedContentPanel
        open={contentPanel}
        rows={draft.localizedInfo}
        onClose={() => setContentPanel(false)}
        onSave={(next) => {
          set("localizedInfo", next);
          setContentPanel(false);
        }}
      />

      {/**
       * ⚠️ `assetId` is "" while the asset is being CREATED, which disables
       * uploading inside the panel rather than breaking it: bytes are filed
       * against a title, and there is no title yet. The row list is still
       * editable, so nothing else about the panel changes — the same reason the
       * ingest block below only renders over a saved asset.
       */}
      <LocalisedImagesPanel
        open={imagesPanel}
        images={draft.images}
        assetId={initial?.assetId ?? ""}
        surface="staff"
        onClose={() => setImagesPanel(false)}
        onSave={(next) => {
          set("images", next);
          setImagesPanel(false);
        }}
        onUploaded={(saved) => set("images", assetToDraft(saved).images)}
      />

      {/* Only over a SAVED asset: an ingest is keyed on assetId and one being
          created has none. Mounted here beside the other panels, but it edits
          nothing in the draft - an ingest is a process, not a field, so it
          neither reads nor writes the form. */}
      {initial && (
        <MediaFilesPanel
          open={mediaFiles}
          assetId={initial.assetId}
          assetTitle={initial.originalTitle || initial.assetId}
          // The SAME derivations the rail draws, handed over rather than
          // recomputed (`BE-32`). Recomputing from the same draft agrees today
          // and is exactly the seam along which two screens drift.
          asset={derived}
          blockers={blockers}
          availability={availability}
          onClose={() => setMediaFiles(false)}
        />
      )}
    </Dialog>
  );
}
